import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, uploadImage } from "../utils/api";
import { useApp } from "../context/AppContext";
import { t } from "../utils/i18n";
import AudienceSelector, { AudienceOption } from "../components/AudienceSelector";
import FriendSelector from "../components/FriendSelector";
import { WishIcon, ThoughtIcon, EmotionIcon, DreamIcon, MemoryIcon, SecretIcon, PrivateIcon, ChosenIcon, SharedGardenIcon, PublicIcon } from "../components/GardenIcons";
import { PencilIcon, TrashIcon } from "../components/messages/icons";

type FieldSpec = { key: string; label: string; type: "text" | "date" | "select"; options?: string[] };

// Suggestions de départ — pour aider à remplir une page blanche, comme les tags de Mes Désirs
// (qui n'a jamais ce problème). Tap → préremplit le titre, ouvre le formulaire s'il ne l'était
// pas déjà. Pas sur toutes les catégories : Pensées/Secrets restent volontairement en écriture
// libre, ce sont des contenus trop personnels pour être suggérés.
const CATEGORY_META: Record<string, { i18nKey: string; Icon: React.ComponentType<{ size?: number; color?: string }>; fields: FieldSpec[]; statuses?: string[]; suggestions?: string[] }> = {
  wish: {
    i18nKey: "garden_cat_wishes", Icon: WishIcon, statuses: ["todo", "preparing", "done"],
    suggestions: [
      "Voyager quelque part de nouveau", "Apprendre une nouvelle compétence", "Organiser une soirée entre amis",
      "M'offrir quelque chose de spécial", "Vivre une expérience inédite", "Passer plus de temps avec quelqu'un",
    ],
    fields: [
      { key: "place", label: "Lieu", type: "text" },
      { key: "desired_date", label: "Date souhaitée", type: "date" },
      { key: "with_whom", label: "Avec qui", type: "text" },
    ],
  },
  thought: { i18nKey: "garden_cat_thoughts", Icon: ThoughtIcon, fields: [] },
  emotion: {
    i18nKey: "garden_cat_emotions", Icon: EmotionIcon,
    fields: [
      { key: "emotion", label: "Émotion", type: "select", options: ["Heureux", "Amoureux", "Calme", "Enthousiaste", "Excité", "Joyeux", "Triste", "Nostalgique", "En colère", "Inquiet", "Perdu", "Fatigué", "Sensible", "Reconnaissant"] },
      { key: "intensity", label: "Intensité", type: "select", options: ["1", "2", "3", "4", "5"] },
    ],
  },
  dream: {
    i18nKey: "garden_cat_dreams", Icon: DreamIcon, statuses: ["todo", "preparing", "done"],
    suggestions: [
      "Changer de vie professionnelle", "Vivre à l'étranger un temps", "Écrire un livre",
      "Créer mon propre projet", "Rencontrer une personne qui compte", "Me sentir enfin apaisé(e)",
    ],
    fields: [
      { key: "category", label: "Catégorie", type: "select", options: ["Voyage", "Vie personnelle", "Carrière", "Projet", "Maison / Lifestyle", "Créativité", "Défi personnel", "Expérience"] },
      { key: "target_date", label: "Date cible", type: "date" },
    ],
  },
  memory: {
    i18nKey: "garden_cat_memories", Icon: MemoryIcon,
    suggestions: [
      "Un voyage inoubliable", "Une rencontre marquante", "Un moment en famille",
      "Une soirée entre amis", "Un accomplissement personnel", "Un instant de bonheur simple",
    ],
    fields: [
      { key: "location", label: "Lieu", type: "text" },
      { key: "people", label: "Personnes présentes", type: "text" },
      { key: "relive", label: "Aimerais-tu revivre ce moment ?", type: "select", options: ["Oui", "Absolument", "Peut-être", "Non"] },
    ],
  },
  secret: { i18nKey: "garden_cat_secrets", Icon: SecretIcon, fields: [] },
};

const inputStyle: React.CSSProperties = { width: "100%", padding: "11px 14px", borderRadius: 10, border: "1px solid #E0E0E0", fontSize: 14, marginBottom: 12, boxSizing: "border-box" };
const labelStyle: React.CSSProperties = { fontSize: 12, fontWeight: 600, color: "#666", display: "block", marginBottom: 6 };

// Un seul écran réutilisé pour les 6 catégories à liste du Jardin Secret (envies/pensées/
// émotions/rêves/souvenirs/secrets) — calqué sur MyAddresses.tsx (même forme d'état, même bascule
// formulaire inline). Seul le jeu de champs affichés change selon `category` (CATEGORY_META).
export default function GardenCategory() {
  const { category = "" } = useParams<{ category: string }>();
  const meta = CATEGORY_META[category];
  const navigate = useNavigate();
  const { lang, user } = useApp();

  const [items, setItems] = useState<any[] | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [fields, setFields] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<string>("");
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [audience, setAudience] = useState("private");
  const [sharedWith, setSharedWith] = useState<string[]>([]);
  const [gardenId, setGardenId] = useState<string>("");
  const [myGardens, setMyGardens] = useState<any[]>([]);
  const [saving, setSaving] = useState(false);

  const load = () => {
    if (!meta) return;
    api.get(`/garden/me/items?category=${category}`).then(d => setItems(d?.items || [])).catch(() => setItems([]));
  };
  useEffect(load, [category]);
  // "Notre Jardin" comme niveau de partage n'a de sens que s'il en existe au moins un — chargé une
  // fois, réutilisé pour toutes les catégories.
  useEffect(() => {
    api.get("/garden/gardens/mine").then(d => {
      const active = (d?.gardens || []).filter((g: any) => g.my_status === "active");
      setMyGardens(active);
      if (active.length === 1) setGardenId(active[0].id);
    }).catch(() => {});
  }, []);

  const resetForm = () => {
    setShowForm(false); setEditingId(null); setTitle(""); setDescription(""); setFields({});
    setStatus(meta?.statuses?.[0] || ""); setMediaUrl(null); setAudience("private"); setSharedWith([]);
  };

  const startEdit = (it: any) => {
    setEditingId(it.id); setTitle(it.title || ""); setDescription(it.description || "");
    setFields(it.fields || {}); setStatus(it.status || ""); setMediaUrl(it.media_url || null);
    setAudience(it.audience || "private"); setGardenId(it.garden_id || (myGardens[0]?.id ?? "")); setShowForm(true);
  };

  const onPickPhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try { setMediaUrl(await uploadImage(file, "garden")); } finally { setUploading(false); }
  };

  const save = async () => {
    if (saving) return;
    setSaving(true);
    const body = { category, title: title.trim() || null, description: description.trim() || null, fields, status: status || null, media_url: mediaUrl, media_type: mediaUrl ? "photo" : null };
    try {
      const data = editingId ? await api.put(`/garden/me/items/${editingId}`, body) : await api.post("/garden/me/items", body);
      if (data?.error) { alert(data.error); return; }
      const id = editingId || data.item?.id;
      if (id) await api.put(`/garden/me/items/${id}/audience`, { audience, shared_with: sharedWith, garden_id: gardenId || undefined });
      resetForm(); load();
    } finally { setSaving(false); }
  };

  const remove = (id: string) => {
    if (!window.confirm(t(lang, "garden_delete_confirm_title"))) return;
    api.delete(`/garden/me/items/${id}`).then(() => load());
  };

  if (!meta) return null;
  const audienceOptions: AudienceOption[] = [
    { key: "private", label: t(lang, "garden_audience_private"), sub: "", icon: PrivateIcon },
    { key: "chosen", label: t(lang, "garden_audience_chosen"), sub: "", icon: ChosenIcon },
    // "Notre Jardin" seulement s'il en existe au moins un — le partager vers rien n'a pas de sens.
    ...(myGardens.length > 0 ? [{ key: "garden", label: t(lang, "garden_audience_garden"), sub: "", icon: SharedGardenIcon }] : []),
    { key: "public", label: t(lang, "garden_audience_public"), sub: "", icon: PublicIcon },
  ];

  return (
    <div style={{ maxWidth: 560, margin: "0 auto", padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 20 }}>
        <button onClick={() => navigate("/garden")} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 20, color: "#666" }}>←</button>
        <meta.Icon size={18} color="#0a0a0a" />
        <span style={{ fontSize: 20, fontWeight: 700, flex: 1 }}>{t(lang, meta.i18nKey)}</span>
        <button onClick={() => (showForm ? resetForm() : (resetForm(), setShowForm(true)))} style={{ border: "none", background: "none", cursor: "pointer", fontSize: 22, color: "#0a0a0a" }}>{showForm ? "✕" : "+"}</button>
      </div>

      {showForm && (
        <div style={{ border: "1px solid #EEE", borderRadius: 14, padding: 16, marginBottom: 16 }}>
          {!!meta.suggestions && !editingId && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: "#999", textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 8 }}>{t(lang, "garden_suggestions_label")}</div>
              <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8 }}>
                {meta.suggestions.map(s => (
                  <button key={s} type="button" onClick={() => setTitle(s)} style={{ padding: "7px 13px", borderRadius: 20, border: title === s ? "1px solid #0a0a0a" : "1px solid #E0E0E0", backgroundColor: title === s ? "#0a0a0a" : "white", color: title === s ? "white" : "#333", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}
          <input value={title} onChange={e => setTitle(e.target.value.slice(0, 150))} placeholder="Titre" style={inputStyle} />
          <textarea value={description} onChange={e => setDescription(e.target.value)} placeholder="Description" rows={3} style={{ ...inputStyle, resize: "vertical" as const }} />

          {meta.fields.map(f => (
            <div key={f.key}>
              <label style={labelStyle}>{f.label}</label>
              {f.type === "select" ? (
                <select value={fields[f.key] || ""} onChange={e => setFields({ ...fields, [f.key]: e.target.value })} style={inputStyle}>
                  <option value="">—</option>
                  {f.options!.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : (
                <input type={f.type} value={fields[f.key] || ""} onChange={e => setFields({ ...fields, [f.key]: e.target.value })} style={inputStyle} />
              )}
            </div>
          ))}

          {meta.statuses && (
            <div>
              <label style={labelStyle}>Statut</label>
              <select value={status} onChange={e => setStatus(e.target.value)} style={inputStyle}>
                {meta.statuses.map(s => <option key={s} value={s}>{t(lang, `garden_status_${s}`)}</option>)}
              </select>
            </div>
          )}

          <label style={labelStyle}>Photo</label>
          <div style={{ marginBottom: 14 }}>
            {mediaUrl && <img src={mediaUrl} alt="" style={{ width: "100%", maxHeight: 160, objectFit: "cover", borderRadius: 10, marginBottom: 8 }} />}
            <input type="file" accept="image/*" onChange={onPickPhoto} disabled={uploading} />
          </div>

          <label style={labelStyle}>Confidentialité</label>
          <AudienceSelector value={audience} onChange={setAudience} options={audienceOptions} />
          {audience === "chosen" && <FriendSelector selected={sharedWith} onToggle={id => setSharedWith(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id])} />}
          {audience === "garden" && myGardens.length > 1 && (
            <select value={gardenId} onChange={e => setGardenId(e.target.value)} style={inputStyle}>
              {myGardens.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
          )}

          <button onClick={save} disabled={saving} style={{ width: "100%", padding: 12, borderRadius: 12, border: "none", backgroundColor: "#0a0a0a", color: "white", fontWeight: 700, fontSize: 14, cursor: "pointer" }}>
            {t(lang, "garden_save")}
          </button>
        </div>
      )}

      {items === null ? (
        <div style={{ textAlign: "center", color: "#999", marginTop: 40 }}>{t(lang, "msg_loading")}</div>
      ) : items.length === 0 && !showForm ? (
        <div style={{ textAlign: "center", marginTop: 60 }}>
          <div style={{ width: 56, height: 56, borderRadius: 28, backgroundColor: "#F5F5F5", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}><meta.Icon size={24} color="#999" /></div>
          <div style={{ fontSize: 13, color: "#999", marginBottom: meta.suggestions ? 16 : 0 }}>{t(lang, "garden_empty")}</div>
          {!!meta.suggestions && (
            <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8, justifyContent: "center", maxWidth: 440, margin: "0 auto" }}>
              {meta.suggestions.map(s => (
                <button key={s} onClick={() => { resetForm(); setShowForm(true); setTitle(s); }} style={{ padding: "7px 13px", borderRadius: 20, border: "1px solid #E0E0E0", backgroundColor: "white", color: "#333", fontSize: 12.5, fontWeight: 600, cursor: "pointer" }}>
                  + {s}
                </button>
              ))}
            </div>
          )}
        </div>
      ) : (
        items.map(it => (
          <div key={it.id} style={{ display: "flex", alignItems: "center", gap: 12, border: "1px solid #EEE", borderRadius: 14, padding: 12, marginBottom: 10 }}>
            {it.media_url ? (
              <img src={it.media_url} alt="" style={{ width: 44, height: 44, borderRadius: 10, objectFit: "cover", flexShrink: 0 }} />
            ) : (
              <div style={{ width: 44, height: 44, borderRadius: 22, backgroundColor: "#F5F5F5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}><meta.Icon size={19} color="#0a0a0a" /></div>
            )}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.title || it.fields?.emotion || "—"}</div>
              {it.description && <div style={{ fontSize: 12, color: "#999", marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.description}</div>}
              {it.status && <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>{t(lang, `garden_status_${it.status}`)}</div>}
            </div>
            <button onClick={() => startEdit(it)} style={{ border: "none", background: "none", cursor: "pointer", display: "flex" }}><PencilIcon size={16} color="#999" /></button>
            <button onClick={() => remove(it.id)} style={{ border: "none", background: "none", cursor: "pointer", display: "flex" }}><TrashIcon size={16} color="#999" /></button>
          </div>
        ))
      )}
    </div>
  );
}
